import { useState } from 'react';

interface EnvelopeProps {
  posterUrl: string;
  movieTitle: string;
  teaserText: string;
  onOpen: () => void;
  isRevealed: boolean;
}

export function Envelope({
  posterUrl,
  movieTitle,
  teaserText,
  onOpen,
  isRevealed,
}: EnvelopeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [contentEmerging, setContentEmerging] = useState(false);

  const handleOpen = () => {
    if (!isOpen) {
      setIsOpen(true);
      // Wait for flap animation to complete before emerging content
      setTimeout(() => {
        setContentEmerging(true);
      }, 600);
      // Wait for full animation before triggering reveal
      setTimeout(() => {
        onOpen();
      }, 1800);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full w-full envelope-perspective px-4">
      {/* Teaser Text - shown above envelope before opening */}
      <div
        className={`mb-6 sm:mb-8 text-center max-w-md transition-all duration-700 ${isOpen ? 'opacity-0 -translate-y-4' : 'opacity-100'}`}
      >
        <p className="text-cream/80 font-serif italic text-base sm:text-lg leading-relaxed">
          "{teaserText}"
        </p>
      </div>

      {/* The Envelope Container - larger for better presence */}
      <div
        className={`relative w-[280px] h-[200px] sm:w-[360px] sm:h-[260px] transition-all duration-1000 ${isRevealed ? 'translate-y-[200px] opacity-0 pointer-events-none' : 'cursor-pointer active:scale-[0.98] sm:hover:scale-[1.02]'}`}
        onClick={handleOpen}
        onTouchEnd={(e) => {
          // Prevent double-tap zoom on mobile
          e.preventDefault();
          handleOpen();
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpen();
          }
        }}
        aria-label="Open envelope to reveal your film"
      >
        {/* Envelope Back (Inside) - dark interior */}
        <div className="absolute inset-0 bg-gradient-to-b from-dark-800 to-dark rounded-lg shadow-2xl z-0"></div>

        {/* The Movie Poster Card (Slides out) - z-40 when emerging to be above the flap */}
        <div
          className={`absolute top-3 left-3 right-3 bottom-3 bg-white rounded-md overflow-hidden transition-all shadow-xl
            ${contentEmerging ? 'envelope-card-emerge z-40' : 'z-10'}
            ${!isOpen ? 'translate-y-0' : ''}
          `}
        >
          <div className="w-full h-full relative">
            {/* The Movie Poster */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${posterUrl})` }}
            ></div>

            {/* Title Overlay */}
            <div
              className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-end justify-center pb-4 transition-opacity duration-500 ${contentEmerging ? 'opacity-100' : 'opacity-0'}`}
            >
              <span className="text-white font-display text-lg sm:text-xl tracking-wide text-center px-4 drop-shadow-lg">
                {movieTitle}
              </span>
            </div>

            {/* Shiny reflection */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 pointer-events-none"></div>
          </div>
        </div>

        {/* Envelope Front (Pocket) - covers bottom half of card */}
        <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden rounded-lg">
          {/* Bottom Pocket with gradient */}
          <div className="absolute bottom-0 w-full h-[55%] bg-gradient-to-t from-primary via-primary to-primary-500 rounded-b-lg shadow-inner flex items-end justify-center pb-4 sm:pb-6">
            <div className="text-cream/30 font-display text-xs sm:text-sm tracking-[0.3em] uppercase select-none">
              Still Love Film
            </div>
          </div>
        </div>

        {/* Wax seal - positioned above the flap (z-50) when closed, hidden when open */}
        <div
          className={`absolute left-1/2 -translate-x-1/2 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 shadow-lg flex items-center justify-center border-2 border-amber-300/30 pointer-events-none transition-opacity duration-300
            ${isOpen ? 'opacity-0 z-20' : 'opacity-100 z-50'}
          `}
          style={{ top: 'calc(45% - 28px)' }}
        >
          <span className="font-serif text-amber-900 font-bold text-xs sm:text-sm tracking-tight">
            SLF
          </span>
        </div>

        {/* Envelope Flap (Top) */}
        <div
          className={`absolute top-0 w-full h-[50%] z-30 transition-transform duration-700 ease-in-out origin-top envelope-transform-style
            ${isOpen ? 'envelope-flap-open' : 'envelope-flap-closed'}
          `}
        >
          {/* Front of Flap - triangular shape effect */}
          <div className="absolute inset-0 bg-gradient-to-b from-primary-600 to-primary rounded-t-lg shadow-lg envelope-backface-hidden">
            {/* Triangular fold effect */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-transparent"></div>
          </div>

          {/* Back of Flap (Visible when open) */}
          <div className="absolute inset-0 bg-gradient-to-b from-dark-700 to-dark-800 rounded-t-lg envelope-flap-back envelope-backface-hidden shadow-inner"></div>
        </div>
      </div>

      {/* Call to action button - only shown before opening */}
      {!isOpen && !isRevealed && (
        <div className="mt-8 sm:mt-10 text-center">
          <p className="text-cream/50 font-serif text-xs sm:text-sm mb-3">
            Your weekly rental has arrived
          </p>
          <button
            onClick={handleOpen}
            className="group px-8 py-3 bg-primary text-cream font-bold rounded-full active:bg-primary-700 sm:hover:bg-primary-500 transition-all shadow-lg touch-manipulation text-sm sm:text-base min-h-[48px] flex items-center gap-2"
          >
            <span>Open Envelope</span>
            <svg
              className="w-4 h-4 transition-transform group-hover:translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
